import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { Award, FileCheck, Building2, Search as SearchIcon, ShoppingBag, Shield, Clock, Users, CheckCircle, ArrowRight, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../components/ui/carousel';
import { PricingSection } from '../components/PricingSection';
import { NewPricingSection } from '../components/NewPricingSection';
import { newPricingStructure } from '../pricingData';
import { TestimonialSlider } from '../components/TestimonialSlider';
import { ProcessSlider } from '../components/ProcessSlider';
import { CTABanner } from '../components/CTABanner';
import { FAQSection } from '../components/FAQSection';
import { services, countryCodes } from '../mock';
import { toast } from 'sonner';
import { validateEmail, validatePhoneNumber, formatPhoneNumber } from '../utils/validation';
import { useScrollAnimation, useCounterAnimation } from '../hooks/useScrollAnimation';
import { fetchGeolocation, submitLead, getCountryDialCode, getCountryFlag } from '../services/api';

const iconMap = {
  'award': Award,
  'file-badge': FileCheck,
  'building-2': Building2,
  'search-check': SearchIcon,
  'shopping-bag': ShoppingBag
};

// Services Section Component with Scroll Animation
const ServicesSection = ({ onOpenPopup }) => {
  const [titleRef, titleVisible] = useScrollAnimation(0.2);
  const [servicesRef, servicesVisible] = useScrollAnimation(0.2);

  const allItems = [
    ...services.map(service => ({...service, type: 'service'})),
    { id: 'cta', type: 'cta' }
  ];

  const ServiceCard = ({ item }) => {
    if (item.type === 'cta') {
      return (
        <div className="bg-gradient-to-br from-orange-600 to-orange-700 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all border border-orange-600 flex flex-col h-full">
          <div className="bg-white/20 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-white mb-3">
            Need Help Choosing?
          </h3>
          <p className="text-base text-white/95 leading-relaxed mb-4 flex-1">
            Get expert guidance on which service is right for your business. Our team is ready to help.
          </p>
          <Button
            onClick={onOpenPopup}
            className="w-full bg-white text-orange-600 hover:bg-gray-100 h-11 text-base font-semibold shadow-lg"
          >
            Get Free Consultation
          </Button>
        </div>
      );
    }

    const Icon = iconMap[item.icon];
    
    // Unique background images for each service
    const serviceImages = {
      1: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&q=80', // Trademark - Legal documents
      2: 'https://images.unsplash.com/photo-1618060932014-4deda4932554?w=800&q=80', // Copyright - Creative art
      3: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80', // Company Formation - Modern office
      4: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80', // Trademark Search - Data analysis
      5: 'https://images.unsplash.com/photo-1556742044-3c52d6e88c62?w=800&q=80', // Amazon - E-commerce
    };
    
    return (
      <Link
        to={item.link}
        className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all group border border-gray-200 h-full flex flex-col overflow-hidden relative"
      >
        {/* Background Image on Hover */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-0">
          <img 
            src={serviceImages[item.id]} 
            alt={item.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-orange-600/95 via-orange-500/90 to-orange-400/85"></div>
        </div>
        
        <div className="relative z-10">
          <div className="bg-gradient-to-br from-primary/10 to-primary/5 w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:from-white/20 group-hover:to-white/10 group-hover:backdrop-blur-sm transition-all">
            <Icon className="text-primary group-hover:text-white" size={28} />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-white transition-colors">
            {item.title}
          </h3>
          <p className="text-base text-gray-600 leading-relaxed mb-3 flex-1 group-hover:text-white/95">{item.description}</p>
          <div className="flex items-center text-primary group-hover:text-white font-semibold text-sm group-hover:gap-2 transition-all">
            Learn More <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </Link>
    );
  };

  return (
    <>
      <div className="container mx-auto px-4">
        <div 
          ref={titleRef}
          className={`text-center mb-16 scroll-animate scroll-fade-in ${titleVisible ? 'visible' : ''}`}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Our Services</h2>
          <p className="text-base text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Comprehensive intellectual property solutions tailored to your business needs
          </p>
        </div>
        
        {/* Mobile: Carousel/Slider */}
        <div className="md:hidden" ref={servicesRef}>
          <Carousel className="w-full max-w-sm mx-auto">
            <CarouselContent>
              {allItems.map((item) => (
                <CarouselItem key={item.id}>
                  <div className="p-1">
                    <ServiceCard item={item} />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious />
            <CarouselNext />
          </Carousel>
        </div>

        {/* Desktop: Grid */}
        <div 
          ref={servicesRef}
          className={`hidden md:grid grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto stagger-children ${servicesVisible ? 'visible' : ''}`}
        >
          {allItems.map((item) => (
            <ServiceCard key={item.id} item={item} />
          ))}
        </div>
      </div>
    </>
  );
};

// Why Choose Us Component with Scroll Animation
const WhyChooseUs = () => {
  const [titleRef, titleVisible] = useScrollAnimation(0.2);
  const [cardsRef, cardsVisible] = useScrollAnimation(0.2);
  const [imagesRef, imagesVisible] = useScrollAnimation(0.2);

  return (
    <section className="py-24 bg-white">
      <div className="container mx-auto px-4">
        <div 
          ref={titleRef}
          className={`text-center mb-12 scroll-animate scroll-fade-in ${titleVisible ? 'visible' : ''}`}
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Us</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            We combine expertise, efficiency, and personalized service to deliver exceptional trademark protection
          </p>
        </div>
        
        {/* Mobile: Carousel */}
        <div className="md:hidden mb-12 px-4" ref={cardsRef}>
          <Carousel className="w-full max-w-sm mx-auto">
            <CarouselContent>
              <CarouselItem>
                <div className="p-2">
                  <div className="relative bg-gradient-to-br from-orange-50 via-white to-orange-50/50 rounded-3xl p-8 shadow-xl border border-orange-100 overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-orange-200/30 rounded-full blur-2xl"></div>
                    <div className="relative text-center">
                      <div className="bg-gradient-to-br from-orange-500 to-orange-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                        <Shield className="text-white" size={36} />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">Expert Protection</h3>
                      <p className="text-base text-gray-600 leading-relaxed mb-4">
                        Years of experience in trademark law ensuring your intellectual property is secured with precision and care
                      </p>
                    </div>
                  </div>
                </div>
              </CarouselItem>
              <CarouselItem>
                <div className="p-2">
                  <div className="relative bg-gradient-to-br from-teal-50 via-white to-teal-50/50 rounded-3xl p-8 shadow-xl border border-teal-100 overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-teal-200/30 rounded-full blur-2xl"></div>
                    <div className="relative text-center">
                      <div className="bg-gradient-to-br from-teal-500 to-cyan-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                        <Clock className="text-white" size={36} />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">Fast Processing</h3>
                      <p className="text-base text-gray-600 leading-relaxed mb-4">
                        Streamlined application process that gets your trademark filed quickly without compromising quality
                      </p>
                    </div>
                  </div>
                </div>
              </CarouselItem>
              <CarouselItem>
                <div className="p-2">
                  <div className="relative bg-gradient-to-br from-slate-50 via-white to-slate-50/50 rounded-3xl p-8 shadow-xl border border-slate-200 overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-slate-200/30 rounded-full blur-2xl"></div>
                    <div className="relative text-center">
                      <div className="bg-gradient-to-br from-slate-600 to-slate-700 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                        <Users className="text-white" size={36} />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">Dedicated Support</h3>
                      <p className="text-base text-gray-600 leading-relaxed mb-4">
                        Personalized guidance throughout your trademark journey with dedicated case managers available when you need them
                      </p>
                    </div>
                  </div>
                </div>
              </CarouselItem>
            </CarouselContent>
            <CarouselPrevious />
            <CarouselNext />
          </Carousel>
        </div>

        {/* Desktop: Grid */}
        <div 
          ref={cardsRef}
          className={`hidden md:grid md:grid-cols-3 gap-8 mb-12 max-w-6xl mx-auto stagger-children ${cardsVisible ? 'visible' : ''}`}
        >
          {/* Card 1 - Expert Protection */}
          <div className="group relative bg-gradient-to-br from-orange-50 via-white to-orange-50/50 rounded-3xl p-8 hover:shadow-2xl transition-all duration-500 border border-orange-100 overflow-hidden">
            {/* Background Image on Hover */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              <img 
                src="https://images.unsplash.com/photo-1505664194779-8beaceb93744?w=800&q=80" 
                alt="Legal Protection"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-orange-600/95 via-orange-500/90 to-orange-400/85"></div>
            </div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-orange-200/30 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>
            <div className="relative">
              <div className="bg-gradient-to-br from-orange-500 to-orange-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 group-hover:bg-white/20 group-hover:backdrop-blur-sm">
                <Shield className="text-white" size={36} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-white transition-colors">Expert Protection</h3>
              <p className="text-base text-gray-600 leading-relaxed mb-6 group-hover:text-white/95">
                Years of experience in trademark law ensuring your intellectual property is secured with precision and care
              </p>
              <div className="flex items-center justify-center gap-2 text-orange-600 group-hover:text-white font-semibold text-sm group-hover:gap-3 transition-all">
                <span>Learn more</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>
          
          {/* Card 2 - Fast Processing */}
          <div className="group relative bg-gradient-to-br from-teal-50 via-white to-teal-50/50 rounded-3xl p-8 hover:shadow-2xl transition-all duration-500 border border-teal-100 overflow-hidden">
            {/* Background Image on Hover */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              <img 
                src="https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=800photo-1460925895917-afdab827c52f?w=800&q=80q=80" 
                alt="Fast Technology"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-teal-600/95 via-cyan-500/90 to-teal-400/85"></div>
            </div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-teal-200/30 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>
            <div className="relative">
              <div className="bg-gradient-to-br from-teal-500 to-cyan-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 group-hover:bg-white/20 group-hover:backdrop-blur-sm">
                <Clock className="text-white" size={36} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-white transition-colors">Fast Processing</h3>
              <p className="text-base text-gray-600 leading-relaxed mb-6 group-hover:text-white/95">
                Streamlined application process that gets your trademark filed quickly without compromising quality
              </p>
              <div className="flex items-center justify-center gap-2 text-teal-600 group-hover:text-white font-semibold text-sm group-hover:gap-3 transition-all">
                <span>Learn more</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>
          
          {/* Card 3 - Dedicated Support */}
          <div className="group relative bg-gradient-to-br from-slate-50 via-white to-slate-50/50 rounded-3xl p-8 hover:shadow-2xl transition-all duration-500 border border-slate-200 overflow-hidden">
            {/* Background Image on Hover */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              <img 
                src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800&q=80" 
                alt="Customer Support"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-700/95 via-slate-600/90 to-slate-500/85"></div>
            </div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-slate-200/30 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>
            <div className="relative">
              <div className="bg-gradient-to-br from-slate-600 to-slate-700 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 group-hover:bg-white/20 group-hover:backdrop-blur-sm">
                <Users className="text-white" size={36} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-white transition-colors">Dedicated Support</h3>
              <p className="text-base text-gray-600 leading-relaxed mb-6 group-hover:text-white/95">
                Personalized guidance throughout your trademark journey with dedicated case managers available when you need them
              </p>
              <div className="flex items-center justify-center gap-2 text-slate-700 group-hover:text-white font-semibold text-sm group-hover:gap-3 transition-all">
                <span>Learn more</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>
        </div>

        {/* Image Section - Modern Gradient Cards */}
        <div 
          ref={imagesRef}
          className={`grid grid-cols-1 md:grid-cols-2 gap-8 mt-16 scroll-animate scroll-scale ${imagesVisible ? 'visible' : ''}`}
        >
          <div className="relative rounded-2xl overflow-hidden shadow-2xl group bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700 h-80">
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-10 left-10 w-48 h-48 bg-white rounded-full blur-3xl animate-pulse-slow"></div>
              <div className="absolute bottom-10 right-10 w-64 h-64 bg-white rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1.5s' }}></div>
            </div>
            <div className="relative h-full flex flex-col items-center justify-center p-8 text-center">
              <div className="bg-white/20 backdrop-blur-sm w-24 h-24 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Award className="text-white" size={48} />
              </div>
              <h3 className="text-3xl font-bold text-white mb-3">Proven Success</h3>
              <p className="text-white/95 text-lg max-w-sm">Join 15,000+ satisfied clients who trust us with their brand protection</p>
              <div className="mt-6 flex items-center gap-8">
                <div>
                  <div className="text-4xl font-bold text-white">95%</div>
                  <div className="text-white/80 text-sm">Success Rate</div>
                </div>
                <div className="w-px h-16 bg-white/30"></div>
                <div>
                  <div className="text-4xl font-bold text-white">15K+</div>
                  <div className="text-white/80 text-sm">Trademarks Filed</div>
                </div>
              </div>
            </div>
          </div>
          <div className="relative rounded-2xl overflow-hidden shadow-2xl group bg-gradient-to-br from-teal-500 via-cyan-600 to-teal-700 h-80">
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-10 right-10 w-48 h-48 bg-white rounded-full blur-3xl animate-pulse-slow"></div>
              <div className="absolute bottom-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
            </div>
            <div className="relative h-full flex flex-col items-center justify-center p-8 text-center">
              <div className="bg-white/20 backdrop-blur-sm w-24 h-24 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Users className="text-white" size={48} />
              </div>
              <h3 className="text-3xl font-bold text-white mb-3">Expert Team</h3>
              <p className="text-white/95 text-lg max-w-sm">Experienced trademark attorneys and specialists dedicated to your success</p>
              <div className="mt-6 flex items-center gap-8">
                <div>
                  <div className="text-4xl font-bold text-white">24-48hr</div>
                  <div className="text-white/80 text-sm">Filing Time</div>
                </div>
                <div className="w-px h-16 bg-white/30"></div>
                <div>
                  <div className="text-4xl font-bold text-white">24/7</div>
                  <div className="text-white/80 text-sm">Support</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Animated Trust Bar Component
const TrustBar = () => {
  const [ref1, count1] = useCounterAnimation(10000, 2000);
  const [ref2, count2] = useCounterAnimation(98, 2000);
  const [ref3, count3] = useCounterAnimation(15, 2000);

  return (
    <section className="py-8 bg-gray-50 border-y border-gray-200">
      <div className="container mx-auto px-4">
        <div className="flex flex-wrap items-center justify-center gap-12 text-center">
          <div ref={ref1}>
            <div className="text-3xl font-bold text-primary mb-1 counter-animate">
              {count1.toLocaleString()}+
            </div>
            <p className="text-sm text-gray-600">Trademarks Filed</p>
          </div>
          <div className="h-12 w-px bg-gray-300 hidden md:block"></div>
          <div ref={ref2}>
            <div className="text-3xl font-bold text-primary mb-1 counter-animate">
              {count2}%
            </div>
            <p className="text-sm text-gray-600">Success Rate</p>
          </div>
          <div className="h-12 w-px bg-gray-300 hidden md:block"></div>
          <div ref={ref3}>
            <div className="text-3xl font-bold text-primary mb-1 counter-animate">
              {count3}+ Years
            </div>
            <p className="text-sm text-gray-600">Experience</p>
          </div>
          <div className="h-12 w-px bg-gray-300 hidden md:block"></div>
          <div>
            <div className="text-3xl font-bold text-primary mb-1">24/7</div>
            <p className="text-sm text-gray-600">Support</p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Hero Form Component (compact version) - Using Centralized API
const HeroForm = () => {
  const navigate = useNavigate();
  const [geoData, setGeoData] = useState(null);
  const [isLoadingGeo, setIsLoadingGeo] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryDialCode: 1,
    phoneNumber: '',
    description: ''
  });
  
  // Validation errors state
  const [errors, setErrors] = useState({
    email: '',
    phoneNumber: ''
  });

  // Fetch geolocation on mount
  useEffect(() => {
    const loadGeo = async () => {
      setIsLoadingGeo(true);
      const result = await fetchGeolocation();
      if (result.success) {
        setGeoData(result);
        const dialCode = getCountryDialCode(result.data.countryCode);
        setFormData(prev => ({ ...prev, countryDialCode: dialCode }));
      }
      setIsLoadingGeo(false);
    };
    loadGeo();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate email
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      setErrors(prev => ({ ...prev, email: emailValidation.error }));
      toast.error(emailValidation.error);
      return;
    }
    
    // Validate phone number
    const phoneValidation = validatePhoneNumber(formData.phoneNumber);
    if (!phoneValidation.isValid) {
      setErrors(prev => ({ ...prev, phoneNumber: phoneValidation.error }));
      toast.error(phoneValidation.error);
      return;
    }
    
    // Clear errors if validation passes
    setErrors({ email: '', phoneNumber: '' });
    
    // Check geolocation loaded
    if (isLoadingGeo) {
      toast.error('Please wait while we fetch your location...');
      return;
    }
    
    if (!geoData || !geoData.data) {
      toast.error('Unable to fetch location. Please refresh and try again.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const result = await submitLead({
        name: formData.name,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        description: formData.description,
        countryDialCode: formData.countryDialCode,
        country: geoData.data.countryCode || 'US'
      }, geoData);
      
      if (result.success) {
        console.log('✅ Form submission successful:', result);
        toast.success(result.message || 'Thank you! We will contact you soon.');
        
        // Use window.location.href instead of React navigate
        if (result.redirectUrl) {
          console.log('🔀 Redirecting to:', result.redirectUrl);
          window.location.href = result.redirectUrl;
        } else {
          console.warn('⚠️ No redirectUrl in result:', result);
        }
      } else {
        toast.error(result.error || 'Failed to submit form');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1">Name *</label>
        <Input
          required
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="John Smith"
          className="h-9 text-sm text-gray-900"
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1">Email *</label>
        <Input
          type="email"
          required
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          placeholder="john@company.com"
          className="h-9 text-sm text-gray-900"
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1">Phone Number *</label>
        <div className="flex gap-2">
          <Select
            value={`+${formData.countryDialCode}`}
            onValueChange={(value) => {
              const dialCode = parseInt(value.replace('+', ''));
              setFormData({ ...formData, countryDialCode: dialCode });
            }}
          >
            <SelectTrigger className="w-24 h-9 text-sm font-medium text-gray-900">
              <SelectValue>
                {(() => {
                  const selected = countryCodes.find(c => c.code === `+${formData.countryDialCode}`);
                  return selected ? `${selected.flag} ${selected.code}` : '🇺🇸 +1';
                })()}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="max-h-60">
              {countryCodes.map((item) => (
                <SelectItem key={item.country} value={item.code} className="text-xs">
                  <span className="flex items-center gap-2">
                    <span>{item.flag}</span>
                    <span>{item.code}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="tel"
            required
            value={formData.phoneNumber}
            onChange={(e) => {
              const formatted = formatPhoneNumber(e.target.value);
              setFormData({ ...formData, phoneNumber: formatted });
              if (errors.phoneNumber) {
                setErrors(prev => ({ ...prev, phoneNumber: '' }));
              }
            }}
            onBlur={() => {
              const validation = validatePhoneNumber(formData.phoneNumber);
              if (!validation.isValid) {
                setErrors(prev => ({ ...prev, phoneNumber: validation.error }));
              }
            }}
            placeholder="5551234567"
            maxLength={10}
            className={`flex-1 h-9 text-sm ${errors.phoneNumber ? 'border-red-500' : ''} text-gray-900`}
          />
        </div>
        {errors.phoneNumber && (
          <p className="text-red-500 text-xs mt-1">{errors.phoneNumber}</p>
        )}
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1">Description</label>
        <Textarea
          required
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="I need help registering my trademark for..."
          rows={2}
          className="resize-none text-sm text-gray-900"
        />
      </div>

      {/* {!isLoadingGeo && geoData && geoData.data && (
        <div className="text-center">
          <p className="text-xs text-gray-600">
            {getCountryFlag(geoData.data.countryCode)} {geoData.data.countryName}
          </p>
        </div>
      )} */}

      <Button 
        type="submit" 
        disabled={isSubmitting}
        className="w-full bg-primary hover:bg-primary/90 h-10 text-sm font-semibold shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
      >
        {isSubmitting ? 'Submitting...' : 'Get Started Today →'}
      </Button>
    </form>
  );
};

export const Home = ({ onOpenPopup }) => {
  return (
    <div className="pt-20">
      <Helmet>
        <title>Trademark Registration Services | Protect Your Brand with Expert Legal Guidance</title>
        <meta name="description" content="Professional trademark registration services with comprehensive protection. Expert legal guidance, trademark searches, and brand protection for businesses nationwide. Start protecting your brand today." />
        <meta name="keywords" content="trademark registration, brand protection, trademark search, intellectual property, USPTO filing, trademark attorney, brand registration" />
        <meta property="og:title" content="Trademark Registration Services | Protect Your Brand" />
        <meta property="og:description" content="Professional trademark registration services with comprehensive protection. Expert legal guidance for businesses nationwide." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Trademark Registration Services | Protect Your Brand" />
        <meta name="twitter:description" content="Professional trademark registration services with comprehensive protection. Expert legal guidance for businesses nationwide." />
      </Helmet>
      {/* Creative Hero Section with Form - Optimized for 1080px */}
      <section className="relative bg-gradient-to-br from-orange-600 via-orange-500 to-orange-700 text-white py-10 md:py-12 overflow-hidden min-h-[calc(100vh-80px)] max-h-[1000px] flex items-center">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1.5s' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
        </div>
        
        {/* Decorative Background Image */}
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1480944657103-7fed22359e1d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGJ1c2luZXNzfGVufDB8fHx8MTc2MjU0ODk4OXww&ixlib=rb-4.1.0&q=85" 
            alt="Background" 
            className="w-full h-full object-cover"
          />
        </div>
        
        {/* Floating Decorative Icons */}
        <div className="absolute inset-0 pointer-events-none hidden lg:block">
          <div className="absolute top-20 left-1/4 animate-float" style={{ animationDelay: '0s' }}>
            <Shield className="w-12 h-12 text-white/20" />
          </div>
          <div className="absolute top-32 right-1/4 animate-float" style={{ animationDelay: '2s' }}>
            <Award className="w-14 h-14 text-white/20" />
          </div>
          <div className="absolute bottom-24 left-1/3 animate-float" style={{ animationDelay: '1s' }}>
            <CheckCircle className="w-12 h-12 text-white/20" />
          </div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center max-w-7xl mx-auto">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              <div className="inline-block bg-white/20 backdrop-blur-md rounded-full px-5 py-2 mb-5 animate-slide-in">
                <span className="text-sm md:text-base font-bold">🏆 Trusted by 10,000+ Businesses Nationwide</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-5 leading-tight animate-slide-in">
                Protect Your Brand<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-gray-100 to-white">Legacy</span>
              </h1>
              <p className="text-base md:text-lg mb-6 text-white/95 leading-relaxed animate-slide-in max-w-2xl">
                Comprehensive trademark protection services that safeguard your business identity and intellectual property rights with expert legal guidance
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start animate-slide-in">
                <Button
                  className="bg-white text-orange-600 hover:bg-gray-100 h-11 px-7 text-base font-semibold shadow-xl"
                  onClick={onOpenPopup}
                >
                  Get Free Consultation
                </Button>
                <Button
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 backdrop-blur-sm h-11 px-7 text-base font-semibold"
                  onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
                >
                  Explore Services
                </Button>
              </div>
            </div>

            {/* Right Form - Compact */}
            <div className="bg-white rounded-2xl p-5 shadow-2xl backdrop-blur-lg animate-slide-in">
              <div className="text-center mb-4">
                <div className="bg-primary/10 w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                  <Shield className="text-primary" size={24} />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-1">Start Your Trademark Registration</h2>
                <p className="text-xs text-gray-600">Fill out the form and we'll contact you within 24 hours</p>
              </div>
              
              <HeroForm />
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 80L60 73.3C120 66.7 240 53.3 360 46.7C480 40 600 40 720 43.3C840 46.7 960 53.3 1080 56.7C1200 60 1320 60 1380 60L1440 60V80H1380C1320 80 1200 80 1080 80C960 80 840 80 720 80C600 80 480 80 360 80C240 80 120 80 60 80H0V80Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Informative Section with Image */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch max-w-7xl mx-auto">
            {/* Left - Trademark/Legal Documents Image */}
            <div className="order-2 lg:order-1">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl h-[500px] group">
                <img 
                  src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=800photo-1454165804606-c3d57bc86b40?w=800&q=80q=80" 
                  alt="Legal documents and trademark registration" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
            </div>

            {/* Right - Content */}
            <div className="order-1 lg:order-2 flex flex-col justify-between">
              <div>
                <div className="inline-block bg-orange-100 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-4">
                  Why Trademark Protection Matters
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Secure Your Business Identity Before It's Too Late
                </h2>
                <p className="text-base text-gray-600 leading-relaxed mb-6">
                  Your brand is your most valuable asset. Without proper trademark protection, you risk losing your business identity to competitors or copycats. Every day without protection is a day your brand remains vulnerable.
                </p>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-4">
                    <div className="bg-orange-100 rounded-full p-2 mt-1">
                      <CheckCircle className="text-primary" size={20} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">Exclusive Rights</h3>
                      <p className="text-sm text-gray-600">Gain nationwide protection and exclusive rights to use your trademark in your industry</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="bg-orange-100 rounded-full p-2 mt-1">
                      <CheckCircle className="text-primary" size={20} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">Legal Protection</h3>
                      <p className="text-sm text-gray-600">Prevent others from using confusingly similar names or logos that could harm your business</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="bg-orange-100 rounded-full p-2 mt-1">
                      <CheckCircle className="text-primary" size={20} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">Brand Value</h3>
                      <p className="text-sm text-gray-600">Increase your business valuation with registered intellectual property assets</p>
                    </div>
                  </div>
                </div>
              </div>

              <Button
                onClick={onOpenPopup}
                className="bg-primary hover:bg-primary/90 h-11 px-8 text-base font-semibold w-fit"
              >
                Protect Your Brand Now
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar with Animated Counters */}
      <TrustBar />

      {/* Why Choose Us with Images */}
      <WhyChooseUs />

      {/* Services Section with Enhanced Design */}
      <section id="services" className="py-24 md:py-32 bg-gradient-to-br from-gray-50 to-gray-100">
        <ServicesSection onOpenPopup={onOpenPopup} />
      </section>

      {/* CTA Banner */}
      <CTABanner onOpenPopup={onOpenPopup} />

      {/* Process Section */}
      <ProcessSlider />

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-block bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">💰 Transparent Pricing</div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Affordable, Flat-Fee Trademark Registration Packages</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">Handled by Licensed Attorneys</p>
          </div>
          <NewPricingSection onOpenPopup={onOpenPopup} pricingData={newPricingStructure} />
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection />

      {/* Testimonials */}
      <TestimonialSlider />

      {/* CTA Section with Background Image */}
      <section className="relative py-20 bg-gradient-to-r from-orange-600 to-orange-700 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1462556791646-c201b8241a94?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGJ1c2luZXNzfGVufDB8fHx8MTc2MjU0ODk4OXww&ixlib=rb-4.1.0&q=85" 
            alt="Background" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl font-bold mb-4">Ready to Protect Your Brand?</h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
            Start your trademark registration today and secure your business identity for years to come
          </p>
          <Button
            onClick={onOpenPopup}
            className="bg-white text-orange-600 hover:bg-gray-100 hover:scale-105 transition-all shadow-xl h-12 px-8 text-base"
          >
            Get Started Now
            <ArrowRight className="ml-2" size={18} />
          </Button>
        </div>
      </section>
    </div>
  );
};